import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { CheckCircle2 } from 'lucide-react';
import { findPasswordToken, setPassword } from 'services/authApi';

const MIN_PASSWORD_LENGTH = 8;

type Phase = 'validating' | 'invalid' | 'form' | 'done';

function GlobusLogo() {
  return (
    <Box sx={{ textAlign: 'center', mb: 3.5 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '13px',
          background: 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 1.5,
          boxShadow: '0 4px 16px rgba(20,116,255,0.38)',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <circle cx="11" cy="11" r="4" fill="white" fillOpacity="0.95" />
          <circle cx="11" cy="3.5" r="2" fill="white" fillOpacity="0.5" />
          <circle cx="11" cy="18.5" r="2" fill="white" fillOpacity="0.5" />
          <circle cx="3.5" cy="11" r="2" fill="white" fillOpacity="0.5" />
          <circle cx="18.5" cy="11" r="2" fill="white" fillOpacity="0.5" />
        </svg>
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 18,
          letterSpacing: '-0.5px',
          color: 'var(--color-text-primary)',
        }}
      >
        Globus
        <Box component="span" sx={{ color: 'var(--color-primary)' }}>
          {' '}
          AI
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mt: 0.25 }}>
        Корпоративное рабочее пространство
      </Typography>
    </Box>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [phase, setPhase] = useState<Phase>('validating');
  const [userName, setUserName] = useState('');
  const [password, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Проверка токена при загрузке
  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('Ссылка некорректна: отсутствует токен.');
      return;
    }
    let cancelled = false;
    findPasswordToken(token)
      .then((info) => {
        if (cancelled) return;
        setUserName(info?.name ?? '');
        setPhase('form');
      })
      .catch(() => {
        if (cancelled) return;
        setPhase('invalid');
        setError(
          'Ссылка недействительна или срок её действия истёк. Запросите новую у администратора.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const validate = (): string => {
    if (password.length < MIN_PASSWORD_LENGTH)
      return `Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов.`;
    if (password !== confirm) return 'Пароли не совпадают.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await setPassword(token, password);
      setPhase('done');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Не удалось установить пароль. Попробуйте позже.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 380 }}>
        <GlobusLogo />

        <Box
          component={phase === 'form' ? 'form' : 'div'}
          onSubmit={phase === 'form' ? handleSubmit : undefined}
          sx={{
            background: '#FFFFFF',
            border: '1px solid rgba(20,116,255,0.12)',
            borderRadius: 'var(--radius-xl)',
            p: '36px',
            boxShadow: '0 8px 40px rgba(20,116,255,0.12), 0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {phase === 'validating' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <CircularProgress size={26} sx={{ color: 'var(--color-primary)', mb: 2 }} />
              <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                Проверяем ссылку…
              </Typography>
            </Box>
          )}

          {phase === 'invalid' && (
            <>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '-0.3px',
                  mb: 0.5,
                  color: 'var(--color-text-primary)',
                }}
              >
                Ссылка недоступна
              </Typography>
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  mb: 2.5,
                  fontSize: 12.5,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  bgcolor: 'var(--color-error-tint)',
                  '& .MuiAlert-icon': { fontSize: 16 },
                }}
              >
                {error}
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/login')}
                sx={submitBtnSx}
              >
                Вернуться ко входу
              </Button>
            </>
          )}

          {phase === 'form' && (
            <>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '-0.3px',
                  mb: 0.5,
                  color: 'var(--color-text-primary)',
                }}
              >
                Установка пароля
              </Typography>
              <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 13, mb: 3 }}>
                {userName
                  ? `${userName}, придумайте пароль для входа в систему.`
                  : 'Придумайте пароль для входа в систему.'}
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2.5,
                    fontSize: 12.5,
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    bgcolor: 'var(--color-error-tint)',
                    '& .MuiAlert-icon': { fontSize: 16 },
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Новый пароль"
                  type="password"
                  fullWidth
                  size="small"
                  value={password}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                  helperText={`Минимум ${MIN_PASSWORD_LENGTH} символов`}
                  InputLabelProps={{ style: { fontSize: 13.5 } }}
                />
                <TextField
                  label="Повторите пароль"
                  type="password"
                  fullWidth
                  size="small"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                  InputLabelProps={{ style: { fontSize: 13.5 } }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={submitBtnSx}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Сохранить пароль'}
              </Button>
            </>
          )}

          {phase === 'done' && (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'rgba(5,150,105,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  color: '#059669',
                }}
              >
                <CheckCircle2 size={26} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '-0.3px',
                  mb: 0.5,
                  color: 'var(--color-text-primary)',
                }}
              >
                Пароль установлен
              </Typography>
              <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 13, mb: 3 }}>
                Теперь вы можете войти в систему с новым паролем.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/login')}
                sx={submitBtnSx}
              >
                Перейти ко входу
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const submitBtnSx = {
  mt: 3,
  py: 1.375,
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '-0.01em',
  borderRadius: 'var(--radius-md)',
  background: 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
  boxShadow: '0 2px 8px rgba(20,116,255,0.30)',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #0F5FE0 0%, #1474FF 100%)',
    boxShadow: '0 4px 16px rgba(20,116,255,0.40)',
  },
  transition: 'all 0.2s ease',
} as const;
